import React from 'react';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import { TrashIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { CollaboratorCombobox, RequiredIndicator } from '@/components/shared';

interface FeedbackListFieldSetProps {
    context: CollaboratorFormContext;
}

const FeedbackListFieldSet: React.FC<FeedbackListFieldSetProps> = ({ context }) => {
    const { control } = useFormContext();

    // Field arrays para gerenciar listas dinamicamente (hooks devem vir antes de qualquer early return)
    const occurrencesFieldArray = useFieldArray({
        control,
        name: 'occurrences'
    });

    const raroFeedbacksFieldArray = useFieldArray({
        control,
        name: 'raroFeedbacks'
    });

    const clientFeedbacksFieldArray = useFieldArray({
        control,
        name: 'clientFeedbacks'
    });

    // Helper para verificar se campo deve aparecer
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Early return se não deve mostrar neste contexto
    if (!shouldShow('occurrences') && !shouldShow('raroFeedbacks') && !shouldShow('clientFeedbacks')) {
        return null;
    }

    // Renderizar seção de feedback com funcionalidade real
    const renderFeedbackSection = (
        fieldName: 'occurrences' | 'raroFeedbacks' | 'clientFeedbacks',
        title: string,
        fieldArray: any,
        responsibleType: 'collaborator' | 'client'
    ) => {
        if (!shouldShow(fieldName)) return null;

        const isRequired = collaboratorSchemaHelpers.isFieldRequired(fieldName, context);

        return (
            <fieldset data-testid={`${fieldName}-fieldset`} className="border p-4 rounded-md">
                <legend className="text-sm font-medium px-1 text-gray-400">
                    {title}
                    {isRequired && <RequiredIndicator show={true} />}
                </legend>
                <div className="pt-2 space-y-4">
                    {fieldArray.fields.map((field: any, index: number) => (
                        <div key={field.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start p-3 border rounded-md bg-gray-50">
                            {/* Data */}
                            <div className="md:col-span-3">
                                <label htmlFor={`${fieldName}-${index}-date`} className="block text-sm font-medium text-gray-700">
                                    Data
                                </label>
                                <Controller
                                    name={`${fieldName}.${index}.date`}
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <input
                                                {...field}
                                                value={field.value || ''}
                                                type="date"
                                                id={`${fieldName}-${index}-date`}
                                                className="input-primary w-full mt-1"
                                            />
                                            {fieldState.error && (
                                                <span className="text-red-500 text-sm mt-1">{fieldState.error.message}</span>
                                            )}
                                        </>
                                    )}
                                />
                            </div>

                            {/* Tipo */}
                            <div className="md:col-span-2">
                                <label htmlFor={`${fieldName}-${index}-type`} className="block text-sm font-medium text-gray-700">
                                    Tipo
                                </label>
                                <Controller
                                    name={`${fieldName}.${index}.type`}
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <select
                                                {...field}
                                                value={field.value || ''}
                                                id={`${fieldName}-${index}-type`}
                                                className="input-primary w-full mt-1"
                                            >
                                                <option value="">Selecione...</option>
                                                <option value="Positivo">Positivo</option>
                                                <option value="Negativo">Negativo</option>
                                                <option value="Neutro">Neutro</option>
                                            </select>
                                            {fieldState.error && (
                                                <span className="text-red-500 text-sm mt-1">{fieldState.error.message}</span>
                                            )}
                                        </>
                                    )}
                                />
                            </div>

                            {/* Responsável */}
                            <div className="md:col-span-6">
                                <label className="block text-sm font-medium text-gray-700">
                                    Responsável
                                </label>
                                {responsibleType === 'collaborator' ? (
                                    <Controller
                                        name={`${fieldName}.${index}.responsibleCollaboratorId`}
                                        control={control}
                                        render={({ field }) => (
                                            <CollaboratorCombobox
                                                selectedId={field.value || ''}
                                                onChange={(collaborator) => field.onChange(collaborator?.id || '')}
                                            />
                                        )}
                                    />
                                ) : (
                                    <Controller
                                        name={`${fieldName}.${index}.responsibleClientName`}
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <>
                                                <input
                                                    {...field}
                                                    value={field.value || ''}
                                                    type="text"
                                                    className="input-primary w-full mt-1"
                                                    placeholder="Nome do responsável"
                                                />
                                                {fieldState.error && (
                                                    <span className="text-red-500 text-sm mt-1">{fieldState.error.message}</span>
                                                )}
                                            </>
                                        )}
                                    />
                                )}
                            </div>

                            {/* Botão Remover */}
                            <div className="flex items-center justify-center pt-6 md:col-span-1">
                                <button
                                    type="button"
                                    onClick={() => fieldArray.remove(index)}
                                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-md transition-colors"
                                    title={`Remover ${title}`}
                                >
                                    <TrashIcon className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Anotação */}
                            <div className="md:col-span-12">
                                <label htmlFor={`${fieldName}-${index}-annotation`} className="block text-sm font-medium text-gray-700">
                                    Anotação
                                </label>
                                <Controller
                                    name={`${fieldName}.${index}.annotation`}
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <textarea
                                                {...field}
                                                value={field.value || ''}
                                                id={`${fieldName}-${index}-annotation`}
                                                className="input-primary w-full mt-1"
                                                rows={3}
                                                placeholder="Descreva o feedback ou ocorrência..."
                                            />
                                            {fieldState.error && (
                                                <span className="text-red-500 text-sm mt-1">{fieldState.error.message}</span>
                                            )}
                                        </>
                                    )}
                                />
                            </div>
                        </div>
                    ))}

                    {/* Botão Adicionar */}
                    <div className="flex justify-start">
                        <button
                            type="button"
                            onClick={() => fieldArray.append({
                                id: `new-${Date.now()}`,
                                date: '',
                                type: '',
                                annotation: '',
                                responsibleCollaboratorId: '',
                                responsibleClientName: ''
                            })}
                            className="flex items-center gap-2 px-4 py-2 text-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-50 transition-colors"
                        >
                            <PlusCircleIcon className="h-5 w-5" />
                            Adicionar {title}
                        </button>
                    </div>
                </div>
            </fieldset>
        );
    };

    return (
        <div className="space-y-6">
            {renderFeedbackSection(
                'occurrences',
                'Ocorrência',
                occurrencesFieldArray,
                'collaborator'
            )}
            {renderFeedbackSection(
                'raroFeedbacks',
                'Feedback Raro',
                raroFeedbacksFieldArray,
                'collaborator'
            )}
            {renderFeedbackSection(
                'clientFeedbacks',
                'Feedback Cliente',
                clientFeedbacksFieldArray,
                'client'
            )}
        </div>
    );
};

export default FeedbackListFieldSet; 